import axios, { CancelTokenSource } from 'axios';
import PatientDocumentComments, {
  PatientMedicalDocumentCommentDtoExtended,
  prepareComments,
  prepareUpsertCommentsDtos
} from 'components/clinical-data/PatientDocumentComments';
import TextArea from 'components/final-form/TextArea';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { FormApi } from 'final-form';
import _ from 'lodash';
import React, { useEffect, useState } from "react";
import { Field, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Icon, List, Loader, Modal, Segment } from "semantic-ui-react";
import {
  setPatientDocumentSetPatientDocumentTextContent,
  setPatientSynopsisReportControlledStatus
} from 'services/patientRecordServices';
import styled from "styled-components";
import { PatientMedicalDocumentCommentDto, PdfResponse, UserDisplayDto } from "ts-types/api.types";
import { errorUtils } from 'utils/errorUtils';

const StyledModal = styled(Modal)`
  height: 90vh;
  width: 90vw !important;
`;

const StyledModalContent = styled(Modal.Content)`
  padding: 0 !important;
  height: 100% !important;

  .ui.inline.loader.active.send-email-in-progress {
    margin-top: -0.25rem;
    margin-left: 0.375rem;
  }
`;

const StyledSegmentGroup = styled(Segment.Group)`
  margin: 0 !important;
  height: 100% !important;
  border: none !important;
`;

const LeftSegment = styled(Segment)`
  width: 70% !important;
  padding: 0 !important;
`;

const RightSegment = styled(Segment.Group)`
  display: flex;
  flex-direction: column;
  width: 30% !important;
  margin: 0 !important;
  border: none !important;
  overflow: hidden;

  .segment-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    label {
      font-weight: bold;
    }

    .form-container {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      padding: 0 0.75rem;
      overflow: auto;
    }

    .form-container > .field {
      height: 100%;
    }

    .form-container .ui.form.textarea {
      height: 100%;

      textarea {
        height: 100%;
      }
    }
  }

  .actions-row {
    flex: 0 0 auto;
    display: flex;
    border-top: 1px solid rgba(34, 36, 38, .15);
    padding: 0.75rem 0.5rem;
    align-items: center;

    button {
      margin-left: 0.5rem;
    }

    .divider {
      flex: 1 1 auto;
    }
  }
`;

const ListContent = styled.div`
  width: 90%;
  display: inline-block;
  align-items: center;
`;

const DownloadSection = styled.div`
  width: 10%;
  display: inline-block;
  align-items: center;
  vertical-align: top;
`;

const StyledListItem = styled(List.Item)`
  padding: 0.75rem !important;
`;

const btnStyle = {
  background: "transparent",
  cursor: "pointer",
  padding: "5px 10px"
};

interface FormData {
  textContent: string
}

interface Props extends MessageSourceProps {
  pdfResponses: Array<PdfResponse>
  modalOpen: boolean,
  onCloseModal: () => void,
  getPdfUrl: (pdfResponse: PdfResponse) => string,

  showSendEmail: boolean,
  sendEmailInProgress: boolean,
  sendEmailTextKey: string,
  sendEmail: (pdfId: string) => string,
  textContent: string,
  comments: PatientMedicalDocumentCommentDto[],
  currentUserDisplay: UserDisplayDto,
  patientId: number,
  documentId: number,
  showSetControlledButton: boolean,
  successfullyChangedReportStatus: () => void
}

const PdfModal = (props: Props) => {

  const {
    pdfResponses,
    modalOpen,
    onCloseModal,
    getMessage,
    getPdfUrl,
    showSendEmail,
    sendEmailInProgress,
    sendEmailTextKey,
    sendEmail,
    textContent,
    patientId,
    documentId,
    showSetControlledButton,
    successfullyChangedReportStatus
  } = props;

  if (!pdfResponses || pdfResponses.length == 0) {
    onCloseModal();
  }

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const [selectedPdfResponse, setSelectedPdfResponse] =
      useState<PdfResponse>(pdfResponses[0]);

  const [newTextContent, setNewTextContent] = useState<string>(textContent);

  const [addingComment, setAddingComment] = useState(false);
  const [editingComments, setEditingComments] = useState(false);

  const [comments, setComments] =
      useState<Partial<PatientMedicalDocumentCommentDtoExtended>[]>([]);

  const [errorMessage, setErrorMessage] =
      useState<string | undefined>(undefined);

  const pdfUrl: string = getPdfUrl(selectedPdfResponse);

  useEffect(() => {
    const docComments = props.comments;
    if (docComments) {
      let comments = prepareComments(docComments);
      setComments(comments);
    }
  }, [])

  const handleChange = (form: FormApi) => {
    const textContent = form.getState().values.textContent;

    setNewTextContent(textContent ? textContent : "");
  };

  const savePatientMedicalDocument = async () => {

    const upsertComments = prepareUpsertCommentsDtos(comments);

    await setPatientDocumentSetPatientDocumentTextContent(
        patientId, documentId, upsertComments, cancelTokenSource);

    onCloseModal();
  }

  const handleError = (error: any) => {

    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.canNotSetControlledStatusOnWrongDocumentType
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };

  const setControlledStatus = async (documentId: number, patientId: number) => {

    setPatientSynopsisReportControlledStatus(documentId, patientId, newTextContent, cancelTokenSource)
        .then(response => {
          successfullyChangedReportStatus();
          onCloseModal();
        })
        .catch((error: any) => handleError(error.response.data));
  }

  const renderForm = ({form}: FormRenderProps) => {
    return (
        <div className="form-container">
          <label htmlFor="textContent">
            {getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}
          </label>
          <Field
              name="textContent"
              className="comment"
              component={TextArea}
              style={{maxHeight: "70vh"}}
          />
          <FormSpy onChange={() => handleChange(form)}/>
        </div>
    );
  }

  const mappedPdfResponses = pdfResponses.map((pdfResponse, index) => (
      <StyledListItem
          key={index}
          active={selectedPdfResponse.pdfId == pdfResponse.pdfId}
          onClick={() => setSelectedPdfResponse(pdfResponse)}
      >
        <List.Icon style={{padding: "10px"}} name="file pdf outline" size="large" verticalAlign="middle"/>
        <List.Content>
          <ListContent>
            <List.Header as="h4">{pdfResponse.pdfName}</List.Header>
            <List.Description as="span">{pdfResponse.pdfSize}</List.Description>
          </ListContent>
          <DownloadSection>
            <Button
                as="a"
                href={getPdfUrl(pdfResponse)}
                download={pdfResponse.pdfName}
                icon
                style={btnStyle}
            >
              <Icon name="download" size="large"/>
            </Button>
          </DownloadSection>
        </List.Content>
      </StyledListItem>
  ));

  return (
      <StyledModal
          open={modalOpen}
          onClose={onCloseModal}
      >
        <StyledModalContent>
          <StyledSegmentGroup horizontal>
            <LeftSegment>
              <embed
                  src={pdfUrl}
                  width="100%"
                  height="100%"
                  type="application/pdf"
              />
            </LeftSegment>
            <RightSegment>
              <div className="segment-body">
                <List divided relaxed selection>
                  {mappedPdfResponses}
                </List>
                {
                    errorMessage
                    && (
                        <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                          {errorMessage}
                        </StyledErrorMessage>
                    )
                }

                <div className="form-container">
                  <label htmlFor="textContent">
                    {getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}
                  </label>
                  <PatientDocumentComments
                      comments={comments}
                      currentUserDisplay={props.currentUserDisplay}
                      setAddingComment={setAddingComment}
                      setEditingComments={setEditingComments}
                      onChange={(comments: Partial<PatientMedicalDocumentCommentDtoExtended>[]) => {
                        setComments(comments);
                      }}
                  />
                </div>

              </div>
              <div className="actions-row">
                {
                    showSendEmail &&
                    <Button compact
                            type="button"
                            primary
                            onClick={() => sendEmail(pdfResponses[0].pdfId)}
                    >
                      {getMessage(sendEmailTextKey)}
                      {
                          sendEmailInProgress &&
                          <Loader className="send-email-in-progress" size="mini" active inline/>
                      }
                    </Button>
                }

                {
                    showSetControlledButton &&
                    <Button compact
                            primary
                            type="button"
                            disabled={editingComments || addingComment}
                            onClick={() => setControlledStatus(documentId, patientId)}
                    >
                      {getMessage("button.setControlled")}
                    </Button>
                }
                <div className="divider"/>
                <Button compact
                        type="button"
                        onClick={onCloseModal}
                >
                  {getMessage("button.close")}
                </Button>
                <Button compact
                        type="button"
                        disabled={!_.isNil(comments) && (addingComment || editingComments)}
                        onClick={() => savePatientMedicalDocument()}
                >
                  {getMessage("button.saveAndClose")}
                </Button>
              </div>
            </RightSegment>
          </StyledSegmentGroup>
        </StyledModalContent>
      </StyledModal>
  );
};

export default withMessages(PdfModal);
